import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [coins, setCoins] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('mt_token') || '');

  const getHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [token]);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setUser(res.data.user);
      setProfile(res.data.profile);
      setSubscription(res.data.subscription);
      setCoins(res.data.coins);
    } catch {
      setUser(null);
      setProfile(null);
      setCoins(null);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (phone, password) => {
    const res = await axios.post(`${API}/auth/login`, { phone, password }, { withCredentials: true });
    setUser(res.data.user);
    setProfile(res.data.profile);
    setToken(res.data.token);
    localStorage.setItem('mt_token', res.data.token);
    return res.data;
  };

  const signupStudent = async (data) => {
    const res = await axios.post(`${API}/auth/signup/student`, data, { withCredentials: true });
    setUser(res.data.user);
    setProfile(res.data.profile);
    setToken(res.data.token);
    localStorage.setItem('mt_token', res.data.token);
    return res.data;
  };

  const signupParent = async (data) => {
    const res = await axios.post(`${API}/auth/signup/parent`, data, { withCredentials: true });
    setUser(res.data.user);
    setToken(res.data.token);
    localStorage.setItem('mt_token', res.data.token);
    return res.data;
  };

  const handleOAuthSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(res.data.user);
    setProfile(res.data.profile);
    setToken(res.data.token);
    localStorage.setItem('mt_token', res.data.token);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getHeaders(), withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
    setProfile(null);
    setSubscription(null);
    setCoins(null);
    setToken('');
    localStorage.removeItem('mt_token');
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user, profile, subscription, coins, loading, token,
    login, signupStudent, signupParent, handleOAuthSession,
    logout, refreshUser, getHeaders, API
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
